import React, { useState, useEffect, useMemo } from "react";
import { useGetDayWiseDataMutation } from "state/api";
import { useTheme } from "@mui/material/styles";
import { RingLoader } from "react-spinners";
import useMediaQuery from "@mui/material/useMediaQuery";
import { DownloadOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  Grid,
} from "@mui/material";
import FlexBetween from "components/FlexBetween";
import * as XLSX from "xlsx";
import { lighten } from "@mui/material/styles";

// Utilities
const getMonthYearLabel = (month, year) => {
  const date = new Date(year, month - 1);
  return date.toLocaleString("default", { month: "long" }) + " " + year;
};

const getFinancialYearLabel = (month, year) => {
  const fyStartYear = month <= 3 ? year - 1 : year;
  return `${fyStartYear}-${(fyStartYear + 1).toString().slice(-2)}`;
};

const getMonthlyYearWiseData = () => {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const current = {
    month: currentMonth,
    year: currentYear,
    label: `${getMonthYearLabel(currentMonth, currentYear)} - FY ${getFinancialYearLabel(currentMonth, currentYear)}`
  };

  let lastMonth = currentMonth - 1;
  let lastMonthYear = currentYear;
  if (lastMonth === 0) {
    lastMonth = 12;
    lastMonthYear -= 1;
  }

  const last = {
    month: lastMonth,
    year: lastMonthYear,
    label: `${getMonthYearLabel(lastMonth, lastMonthYear)} - FY ${getFinancialYearLabel(lastMonth, lastMonthYear)}`
  };

  return [current, last];
};

const DailyWise = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [getDayWiseData, { isLoading }] = useGetDayWiseDataMutation();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [monthYearLabel, setMonthYearLabel] = useState("");
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ 
    rm: "", 
    mr: "", 
    product: "" 
  });
  const [daysInMonth, setDaysInMonth] = useState(31);
  const [activeMonth, setActiveMonth] = useState({ month: 0, year: 0 });

  const handleFetch = async (month, year, label) => {
    try {
      setActiveMonth({ month, year });
      const response = await getDayWiseData({ month, year });
      
      if (response?.data?.success) {
        const days = new Date(year, month, 0).getDate();
        setDaysInMonth(days);
        setData(response.data.data);
        setFilteredData(response.data.data);
        setMonthYearLabel(label);
        setError(null);
      } else {
        setError("No data found for this month/year.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to fetch data");
    }
  };

  useEffect(() => {
    const [current] = getMonthlyYearWiseData();
    handleFetch(current.month, current.year, current.label);
  }, []);

  // Apply filters
  useEffect(() => {
    let result = data;
    
    if (filters.rm) {
      result = result.filter(item => 
        item.RmMgName?.toLowerCase().includes(filters.rm.toLowerCase())
      );
    }
    
    if (filters.mr) {
      result = result.filter(item => 
        item.MrName?.toLowerCase().includes(filters.mr.toLowerCase())
      );
    }
    
    if (filters.product) {
      result = result.filter(item => 
        item.Product?.toLowerCase() === filters.product.toLowerCase()
      );
    }
    
    setFilteredData(result);
  }, [data, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({ rm: "", mr: "", product: "" });
  };

  const downloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "DailyWise Data");
    XLSX.writeFile(workbook, `dailywise_report_${activeMonth.month}_${activeMonth.year}.xlsx`);
  };

  const monthlyOptions = getMonthlyYearWiseData();

  // Summary statistics
  const summaryStats = useMemo(() => {
    const stats = {
      totalVisits: 0,
      indocappVisits: 0,
      foliregcdVisits: 0,
      maxScoreDay: { day: 1, value: 0 },
      activeDoctors: new Set(),
      activeMRs: new Set(),
    };

    filteredData.forEach(row => {
      // Count visits per day
      for (let day = 1; day <= daysInMonth; day++) {
        const indocappVal = parseInt(row[`Indocapp_Day_${day}`]) || 0;
        const foliregcdVal = parseInt(row[`Foliregcd_Day_${day}`]) || 0;
        const totalDay = indocappVal + foliregcdVal;
        
        stats.totalVisits += totalDay;
        stats.indocappVisits += indocappVal;
        stats.foliregcdVisits += foliregcdVal;
        
        if (totalDay > stats.maxScoreDay.value) {
          stats.maxScoreDay = { day, value: totalDay };
        }
      }
      
      // Track active entities
      if (row.DoctName && row.DoctName !== "NA") stats.activeDoctors.add(row.DoctName);
      if (row.MrName) stats.activeMRs.add(row.MrName);
    });

    return stats;
  }, [filteredData, daysInMonth]);

  // Get day of week for styling
  const getDayOfWeek = (day) => {
    if (!activeMonth.month || !activeMonth.year) return "";
    const date = new Date(activeMonth.year, activeMonth.month - 1, day);
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <RingLoader color={theme.palette.primary.main} size={100} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h6" color="error">{error}</Typography>
        <Button variant="contained" onClick={() => window.location.reload()} sx={{ mt: 2 }}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box  p={{ xs: 1, md: 3 }}>
      {/* Header */}
      
      <Box 
  sx={{ 
    backgroundColor: theme.palette.background.paper, 
    borderRadius: "12px", 
    p: 3, 
    mb: 3,
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
  }}
>
  <Grid container justifyContent="space-between" alignItems="center" spacing={3}>
    {/* Left-aligned content */}
    <Grid item>
      <Button 
        variant="outlined" 
        onClick={() => navigate(-1)}
        sx={{ 
          fontWeight: 600,
          borderRadius: "8px",
          py: 1,
          px: 2,
          borderWidth: "2px",
          "&:hover": { borderWidth: "2px" }
        }}
      >
        ⬅ Back to Dashboard
      </Button>
    </Grid>
    
    {/* Centered content */}
    <Grid item>
      <Box textAlign="center">
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700, 
            color: theme.palette.secondary.main,
            textShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}
        >
          Daily Performance Report
        </Typography>
        {monthYearLabel && (
          <Typography variant="subtitle1" sx={{ color: theme.palette.text.secondary, mt: 1 }}>
            {monthYearLabel}
          </Typography>
        )}
      </Box>
    </Grid>
    
    {/* Right-aligned content */}
    <Grid item>
      <Box display="flex" justifyContent="flex-end" gap={1} flexWrap="wrap">
        {monthlyOptions.map((option, i) => (
          <Chip
            key={i}
            label={option.label.split(" - ")[0]}
            onClick={() => handleFetch(option.month, option.year, option.label)}
            sx={{
              fontWeight: 600,
              backgroundColor: 
                option.month === activeMonth.month ? 
                theme.palette.primary.main : 
                theme.palette.grey[200],
              color: 
                option.month === activeMonth.month ? 
                "white" : 
                theme.palette.text.primary,
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
                color: "white"
              }
            }}
          />
        ))}
      </Box>
    </Grid>
  </Grid>
</Box>

      {/* Summary Cards */}
      <Box mb={3}>
  <Grid container spacing={2} justifyContent="space-between">
    <Grid item xs={12} sm={5.8} md={2.8} lg={2.8} xl={2.8}>
      <Paper sx={{ 
        p: 2, 
        borderRadius: "12px", 
        backgroundColor: lighten(theme.palette.primary.main, 0.1),
        height: "100%"
      }}>
        <Typography variant="subtitle2" color="textSecondary">TOTAL VISITS</Typography>
        <Typography variant="h4" fontWeight={700}>
          {summaryStats.totalVisits}
        </Typography>
        <Box display="flex" mt={1} gap={1} flexWrap="wrap">
          <Chip 
            label={`Indocapp: ${summaryStats.indocappVisits}`} 
            size="small" 
            sx={{ backgroundColor: lighten(theme.palette.success.light, 0.1) }}
          />
          <Chip 
            label={`Foliregcd: ${summaryStats.foliregcdVisits}`} 
            size="small" 
            sx={{ backgroundColor: lighten(theme.palette.info.light, 0.1) }}
          />
        </Box>
      </Paper>
    </Grid>
    
    <Grid item xs={12} sm={5.8} md={2.8} lg={2.8} xl={2.8}>
      <Paper sx={{ 
        p: 2, 
        borderRadius: "12px", 
        backgroundColor: lighten(theme.palette.success.light, 0.1),
        height: "100%"
      }}>
        <Typography variant="subtitle2" color="textSecondary">ACTIVE DOCTORS</Typography>
        <Typography variant="h4" fontWeight={700}>
          {summaryStats.activeDoctors.size}
        </Typography>
        <Typography variant="caption" color="textSecondary" mt={1}>
          Engaged in the period
        </Typography>
      </Paper>
    </Grid>
    
    <Grid item xs={12} sm={5.8} md={2.8} lg={2.8} xl={2.8}>
      <Paper sx={{ 
        p: 2, 
        borderRadius: "12px", 
        backgroundColor: lighten(theme.palette.warning.light, 0.1),
        height: "100%"
      }}>
        <Typography variant="subtitle2" color="textSecondary">ACTIVE MRs</Typography>
        <Typography variant="h4" fontWeight={700}>
          {summaryStats.activeMRs.size}
        </Typography>
        <Typography variant="caption" color="textSecondary" mt={1}>
          Participating in visits
        </Typography>
      </Paper>
    </Grid>
    
    <Grid item xs={12} sm={5.8} md={2.8} lg={2.8} xl={2.8}>
      <Paper sx={{ 
        p: 2, 
        borderRadius: "12px", 
        backgroundColor: lighten(theme.palette.info.light, 0.1),
        height: "100%"
      }}>
        <Typography variant="subtitle2" color="textSecondary">PEAK ACTIVITY DAY</Typography>
        <Typography variant="h4" fontWeight={700}>
          Day {summaryStats.maxScoreDay.day}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {summaryStats.maxScoreDay.value} visits
        </Typography>
      </Paper>
    </Grid>
  </Grid>
</Box>

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: "12px" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Filter by RM"
              name="rm"
              value={filters.rm}
              onChange={handleFilterChange}
              size="small"
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Filter by MR"
              name="mr"
              value={filters.mr}
              onChange={handleFilterChange}
              size="small"
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Product</InputLabel>
              <Select
                name="product"
                value={filters.product}
                onChange={handleFilterChange}
                label="Product"
              >
                <MenuItem value="">All Products</MenuItem>
                <MenuItem value="Indocapp">Indocapp</MenuItem>
                <MenuItem value="Foliregcd">Foliregcd</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={2} textAlign="right">
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button variant="outlined" onClick={clearFilters}>
                Clear
              </Button>
              <Button 
                variant="contained" 
                startIcon={<DownloadOutlined />}
                onClick={downloadExcel}
                sx={{ minWidth: 120 }}
              >
                Export
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: "12px", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 400px)", position: "relative" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.grey[500] }}>RM</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.grey[500] }}>MR</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.grey[500] }}>Doctor</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.grey[500] }}>Product</TableCell>
                
                {/* Day columns with weekday indicators */}
                {[...Array(daysInMonth)].map((_, i) => {
                  const day = i + 1;
                  const dayOfWeek = getDayOfWeek(day);
                  const isWeekend = dayOfWeek === "Sat" || dayOfWeek === "Sun";
                  
                  return (
                    <TableCell 
                      key={i} 
                      align="center"
                      sx={{ 
                        fontWeight: 700, 
                        backgroundColor: isWeekend ? theme.palette.grey[500] : theme.palette.grey[500],
                        minWidth: 50 
                      }}
                    >
                      <Box display="flex" flexDirection="column" alignItems="center">
                        <span>{day}</span>
                        <span style={{ 
                          fontSize: "0.7rem", 
                          color: isWeekend ? theme.palette.error.main : theme.palette.text.secondary 
                        }}>
                          {dayOfWeek}
                        </span>
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            
            <TableBody>
              {filteredData.length > 0 ? (
                filteredData.map((row, index) => (
                  <TableRow 
                    key={index} 
                    sx={{ 
                      '&:nth-of-type(even)': { backgroundColor: theme.palette.action.hover },
                      '&:hover': { backgroundColor: theme.palette.action.selected }
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{row.RmMgName || "-"}</TableCell>
                    <TableCell>{row.MrName || "-"}</TableCell>
                    <TableCell>{row.DoctName || "-"}</TableCell>
                    <TableCell>
                      <Chip 
                        label={row.Product || "-"} 
                        size="small" 
                        sx={{ 
                          backgroundColor: 
                            row.Product === "Indocapp" ? 
                            lighten(theme.palette.success.light, 0.1) : 
                            lighten(theme.palette.info.light, 0.1),
                          fontWeight: 500
                        }} 
                      />
                    </TableCell>
                    
                    {/* Day values */}
                    {[...Array(daysInMonth)].map((_, i) => {
                      const day = i + 1;
                      const indocappVal = parseInt(row[`Indocapp_Day_${day}`]) || 0;
                      const foliregcdVal = parseInt(row[`Foliregcd_Day_${day}`]) || 0;
                      const value = row.Product === "Indocapp" ? indocappVal : 
                                   row.Product === "Foliregcd" ? foliregcdVal : 
                                   (indocappVal || foliregcdVal || 0);
                      
                      return (
                        <TableCell 
                          key={i} 
                          align="center"
                          sx={{ 
                            fontWeight: "bold",
                            backgroundColor: value > 0 ? 
                              (row.Product === "Indocapp" ? 
                                lighten(theme.palette.success.light, 0.1) : 
                                lighten(theme.palette.info.light, 0.1)) : 
                              "inherit",
                            color: value > 0 ? theme.palette.getContrastText(
                              row.Product === "Indocapp" ? 
                                lighten(theme.palette.success.light, 0.1) : 
                                lighten(theme.palette.info.light, 0.1)
                            ) : "inherit"
                          }}
                        >
                          {value > 0 ? value : "-"}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={daysInMonth + 4} align="center" sx={{ py: 4 }}>
                    <Typography variant="h6" color="textSecondary">
                      No matching records found
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Try adjusting your filters
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Table Footer */}
        <Box 
          sx={{ 
            p: 2, 
            backgroundColor: theme.palette.grey[500], 
            display: "flex", 
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <Typography variant="body2" color="textSecondary">
            Showing {filteredData.length} of {data.length} records
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Scores: 4 = First Visit, 6 = Subsequent Visit
          </Typography>
        </Box>
      </Paper>

      {/* Legend */}
      <Box mt={2} display="flex" justifyContent="center" gap={2} flexWrap="wrap">
        <Box display="flex" alignItems="center">
          <Box 
            sx={{ 
              width: 20, 
              height: 20, 
              backgroundColor: lighten(theme.palette.success.light, 0.1),
              mr: 1,
              borderRadius: "4px"
            }} 
          />
          <Typography variant="caption">Indocapp Visits</Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Box 
            sx={{ 
              width: 20, 
              height: 20, 
              backgroundColor: lighten(theme.palette.info.light, 0.1),
              mr: 1,
              borderRadius: "4px"
            }} 
          />
          <Typography variant="caption">Foliregcd Visits</Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Box 
            sx={{ 
              width: 20, 
              height: 20, 
              backgroundColor: theme.palette.grey[200],
              mr: 1,
              borderRadius: "4px"
            }} 
          />
          <Typography variant="caption">Weekend Days</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DailyWise;